import { useEffect as useEffectQ, useState as useStateQ } from "react";
import { Api } from "./api";
import { Banner, Icon } from "./components";

// My Quota — single page that shows every AI feature's meter in one
// place. Per-feature limits are deliberately separate (so a burst of
// polishing doesn't lock you out of chat) but having them side-by-side
// gives the user a complete picture of where they stand and when their
// next slot opens up. Each meter has its own scope/period so chat
// (per-user, hourly) and polish (per-community, monthly) render with
// the right labels.

function _fmtResetQ(reset_at, period_label) {
  if (!reset_at) return `no usage ${period_label || ""}`.trim();
  const ms = new Date(reset_at).getTime() - Date.now();
  if (Number.isNaN(ms)) return "";
  if (ms <= 0) return "a slot is opening up now";
  if (ms < 60 * 60 * 1000) {
    const mins = Math.max(1, Math.round(ms / 60000));
    return `next slot opens in ${mins} minute${mins === 1 ? "" : "s"}`;
  }
  if (ms < 24 * 60 * 60 * 1000) {
    const when = new Date(reset_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return `next slot opens at ${when}`;
  }
  const days = Math.max(1, Math.round(ms / (24 * 60 * 60 * 1000)));
  return `next slot opens in ${days} day${days === 1 ? "" : "s"}`;
}

function MyQuota({ user }) {
  const [state, setState] = useStateQ({ loading: true, error: null, meters: [] });
  // Tick once a minute so the relative "resets in N minutes" stays
  // current without forcing a page reload.
  const [tick, setTick] = useStateQ(0);

  // background=true is used by the 2-minute auto-refresh tick: it
  // keeps the visible meters on screen instead of blanking them to
  // "Loading…" for the duration of the round-trip, and swallows
  // transient fetch errors instead of wiping the meters with a
  // red banner (CODE_REVIEW #274).
  //
  // Strict-bool gate so an accidental `onClick={reload}` — which
  // passes a React SyntheticEvent as the first arg — doesn't get
  // treated as a background refresh and silently swallow click-time
  // errors (codex P2 on PR #308).
  const reload = (background = false) => {
    const isBackground = background === true;
    if (!isBackground) setState((cur) => ({ ...cur, loading: true, error: null }));
    window.Api.quota()
      .then((data) => setState({ loading: false, error: null, meters: data.meters || [] }))
      .catch((err) => {
        if (isBackground) return;
        setState({ loading: false, error: err.message || "Couldn't load.", meters: [] });
      });
  };
  useEffectQ(() => { reload(); }, []);
  useEffectQ(() => {
    const i = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(i);
  }, []);
  // Light auto-refresh every 2 minutes so the numbers stay accurate
  // if the user leaves the page open.
  useEffectQ(() => {
    const i = setInterval(() => reload(true), 120000);
    return () => clearInterval(i);
  }, []);

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 760 }}>
        <Banner icon="spark" tone="accent">
          <b>Your Compass quota.</b> Each AI feature has its own cap so a burst
          on one doesn't lock you out of another. The meters roll forward as
          earlier uses age out — no fixed clock to wait for.
        </Banner>

        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>Meters</div>
          <button className="btn btn--ghost btn--sm" onClick={() => reload()}>
            <Icon name="history" size={14} />&nbsp; Refresh
          </button>
        </div>

        {state.loading && (
          <div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div>
        )}
        {state.error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)" }}>{state.error}</div>
        )}
        {!state.loading && state.meters.length === 0 && !state.error && (
          <div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>
            No AI features configured.
          </div>
        )}
        {!state.loading && state.meters.length > 0 && (
          <div className="stack-sm" data-tick={tick}>
            {state.meters.map((m) => <QuotaMeterCard key={m.key} meter={m} />)}
          </div>
        )}

        <div className="card card--pad-lg" style={{ background: "var(--bg-2)" }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>How the caps work</div>
          <div className="text-sm text-mute" style={{ lineHeight: 1.55 }}>
            Each meter tracks a <i>rolling</i> window, not a fixed clock — so as the earliest
            use ages off, a slot opens automatically. Chat is per-user / per-hour; Project
            Builder polish is shared across your community and scales with the household
            count. Hitting a cap is rare and the meter always tells you when the next slot opens.
          </div>
        </div>
      </div>
    </div>
  );
}

function QuotaMeterCard({ meter }) {
  const pct = meter.limit > 0 ? Math.min(100, Math.round((meter.used / meter.limit) * 100)) : 0;
  const atLimit = meter.remaining <= 0;
  const nearLimit = !atLimit && meter.remaining <= Math.max(1, Math.floor(meter.limit * 0.2));
  const tone = atLimit ? "var(--danger)" : (nearLimit ? "var(--accent)" : "color-mix(in oklab, var(--accent, #c25e3a) 60%, transparent)");
  const periodLabel = meter.period_label || "in this window";
  return (
    <div className="card card--pad-lg">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div className="row" style={{ gap: 10, alignItems: "flex-start" }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: "var(--accent-soft)",
            color: "var(--accent, #c25e3a)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Icon name={meter.icon || "spark"} size={18} />
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{meter.label}</div>
            <div className="text-sm text-mute" style={{ marginTop: 2 }}>{meter.description}</div>
            {meter.scope && (
              <div className="text-xs text-mute" style={{ marginTop: 4, fontStyle: "italic" }}>{meter.scope}</div>
            )}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontWeight: 700, fontSize: 22, lineHeight: 1, color: atLimit ? "var(--danger)" : "var(--ink)" }}>
            {meter.used} <span style={{ color: "var(--mute)", fontSize: 14, fontWeight: 500 }}>/ {meter.limit}</span>
          </div>
          <div className="text-xs text-mute" style={{ marginTop: 4 }}>{periodLabel}</div>
        </div>
      </div>
      <div style={{ height: 5, marginTop: 10, background: "var(--bg-2)", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${pct}%`, background: tone, transition: "width 200ms ease" }} />
      </div>
      <div className="row" style={{ justifyContent: "space-between", marginTop: 8, fontSize: 13, flexWrap: "wrap", gap: 6 }}>
        <span style={{ color: atLimit ? "var(--danger)" : "var(--ink)", fontWeight: atLimit ? 600 : 400 }}>
          {atLimit ? `At the cap ${periodLabel}` : `${meter.remaining} left ${periodLabel}`}
        </span>
        <span className="text-mute">{_fmtResetQ(meter.reset_at, periodLabel)}</span>
      </div>
    </div>
  );
}

export { MyQuota };
